import React, { useEffect, useMemo, useState } from "react";
import Select from "../../../../components/Select";
import { Slider } from "../../../../components/Slider";
import { H2, H4 } from "../../../../components/Heading";
import { Button } from "../../../../components/Button";
import { Box } from "../../../../components/Box";
import { GrayCard } from "../../../../components/Card";
import { Flex } from "../../../../components/Flex";
import { CgChevronRight } from "react-icons/cg";
import { Paragraph } from "../../../../components/Paragraph";
import { Input } from "../../../../components/Input";
import { DatePicker } from "../../../../components/DatePicker";
import { DateTime } from "luxon";
import { Icon } from "../../../../components/Icon";
import { CardLine } from "../../../IndividualView";
import { Formik } from "formik";
import { useGetProfileList } from "../../../../data/useGetProfileList";
import { useCommonTitles } from "../../../../data/useCommonTitles";
import { MIN_AGE_APPLICATION } from "../../../../utils/constants";
import { useCountries } from "../../../../data/useCountries";
import { useEntityTypes } from "../../../../data/useEntityTypes";
import { useCompanyStatusOptions } from "../../../../data/fallbackPayloads/useCompanyStatusOptions";
import { TextArea } from "../../../../components/TextArea";
import { Label } from "../../../../components/Label";
import { Toggle } from "../../../../components/Toggle";
import { useGetProfileRelationTypes } from "../../../../data/useGetProfileRelationTypes";
import { useCreateProfile } from "../../../../data/useCreateProfile";
import {
  AddressPayload,
  CreateProfileRequest,
} from "../../../../types/Profile";
import { Modal } from "../../../../components/Modal";
import { useAddProfile } from "../../../../data/useAddProfile";
import { useNavigate } from "react-router-dom";
import { queryClient } from "../../../../queryClient";

type Option = {
  label: string;
  value: string;
};

export const PARTY_TYPE_OPTIONS: Option[] = [
  { label: "Natural person", value: "individual" },
  { label: "Entity", value: "entity" },
];

interface Props {
  addRelationshipBoxOpen: boolean;
  setAddRelationshipBoxOpen: (value: boolean) => void;
  firstProfile: any;
}
const CREATE_NEW_OPTION: Option = {
  label: "Create New Profile",
  value: "CREATE_NEW_PROFILE",
};

const INITIAL_FORM_VALUES = {
  partyType: "",
  profile: null,
  profileLabel: "",
  dateOfBirth: "",
  firstName: "",
  lastName: "",
  email: "",
  gender: "",
  title: "",
  middleName: "",
  name: "",
  country: null,
  entityType: null,
  status: "",
  registrationNumber: null,
  dateOfRegistration: "",
  direction: "",
  controls: {},
};

const RelationshipSlider = ({
  addRelationshipBoxOpen,
  setAddRelationshipBoxOpen,
  firstProfile,
}: Props) => {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const { data: profileList } = useGetProfileList({ all: true });
  const [partyType, setPartyType] = useState(null);
  const { data: countries } = useCountries();
  const { data: entityTypes } = useEntityTypes();
  const [addedProfile, setAddedProfile] = useState(null);
  const { options: companyStatusOptions } = useCompanyStatusOptions();
  const [filterByEntityType, setFilterByEntityType] = useState(true);

  const entityTypeId = useMemo(() => {
    if (!entityTypes?.data || !firstProfile?.entity_type) return null;

    const match = entityTypes.data.find(
      (et: any) => et.name === firstProfile.entity_type,
    );

    return match?.id ?? null;
  }, [entityTypes, firstProfile]);

  const relationEntityType = filterByEntityType ? entityTypeId : null;

  // `firstProfile` arrives in two shapes: an org-chart node (has `label`) or,
  // from Profile details > Create > Add relationship, the raw profile record
  // (no `label` — `name` for an entity, first/last name for a person).
  const firstProfileName =
    firstProfile?.label ??
    firstProfile?.display_name ??
    (firstProfile?.name ||
      [firstProfile?.first_name, firstProfile?.last_name]
        .filter(Boolean)
        .join(" "));

  const { data: profileRelationTypes } =
    useGetProfileRelationTypes(relationEntityType);

  const { mutate: createProfile } = useCreateProfile();
  const { mutate: addNewProfile } = useAddProfile();
  const [showSuccess, setShowSuccess] = useState(false);
  const [addedProfileId, setAddedProfileId] = useState(null);
  const [source, setSource] = useState(null);
  const [target, setTarget] = useState(null);

  useEffect(() => {
    if (addRelationshipBoxOpen) {
      setStep(1);
      setPartyType(null);
      setAddedProfile(null);
      setAddedProfileId(null);
      setSource(null);
      setTarget(null);
      setFilterByEntityType(true);
    }
  }, [addRelationshipBoxOpen]);

  const entityTypeOptions =
    entityTypes?.data.map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];

  const genderOptions = [
    { value: "female", label: "Female" },
    { value: "male", label: "Male" },
    { value: "other", label: "Other" },
  ];

  const { data: titlesData } = useCommonTitles();
  const countryOptions =
    countries?.data.map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];

  const titleOptions: Option[] =
    titlesData?.data?.map(({ name }: any) => ({
      label: name,
      value: name,
    })) ?? [];
  const handleCloseSuccess = () => {
    setShowSuccess(false);
    closeForm();
    navigate(`/profile/${firstProfile.id}/org`);
  };

  const addressPayload: AddressPayload[] = [];

  const createNewProfile = (values: any) => {
    const dob =
      values.values.date_of_birth &&
      DateTime.fromISO(values.values.date_of_birth).toFormat("yyyy-MM-dd");

    const payload: CreateProfileRequest = {
      _type: values.values.partyType,
      first_name: values.values.firstName ?? null,
      middle_name: values.values.middleName ?? null,
      last_name: values.values.lastName ?? null,
      gender: values.values.gender ?? null,
      date_of_birth: dob || null,
      email: values.values.email ?? null,
      title: values.values.title ?? null,
      name: values.values.name || null,
      default: false,
      address: addressPayload,
      relation: [],
      entity_type: values.values.entityType ?? null,
      registration_number: values.values.registrationNumber || null,
      date_of_incorporation: values.values.dateOfIncorporation,
      country: values.values.country ?? null,
    };

    createProfile(payload, {
      onSuccess: (res: any) => {
        const id = res.data?.id ?? res.data?.data?.id;
        setAddedProfileId(id);
      },
      onError: (err) => {
        console.error("Failed to create person profile", err);
      },
    });
  };

  const handleNext = (values) => {
    if (isNextDisabled(values.values)) return;

    if (step === 2 && values.values.profileLabel === "Create New Profile") {
      createNewProfile(values);
    }

    if (step === 1) {
      setStep(3);
    } else {
      if (step < 4) setStep(step + 1);
      else {
        setAddRelationshipBoxOpen(false);
      }
    }
    values.values.profileLabel === "Create New Profile" &&
    values.values.partyType === "entity"
      ? setAddedProfile(values.values.name)
      : values.values.profileLabel === "Create New Profile" &&
        values.values.partyType === "individual"
      ? setAddedProfile(values.values.firstName + " " + values.values.lastName)
      : setAddedProfile(values.values.profileLabel);
    setAddedProfileId(values.values.profile);
  };

  const handleSubmit = (props) => {
    const onComplete = () => {
      queryClient.invalidateQueries({
        queryKey: ["orgData"],
      });
    };

    const controls = props.controls;
    const payload = Object.entries(controls)
      .filter(([_, c]: any) => c?.enabled)
      .map(([key, control]: any) => ({
        relationship_type: Number(key),
        source: Number(source),
        target: Number(target),
        percentage: control.percentage ? Number(control.percentage) : null,
        description: control.description ? control.description : null,
        from_date: null,
        to_date: null,
      }));
    addNewProfile(payload, {
      onSuccess: (res: any) => {
        setShowSuccess(true);
        onComplete();
      },
      onError: (err) => {
        console.error("Failed to create person profile", err);
      },
    });
  };

  const relationshipOptions: Option[] = React.useMemo(() => {
    if (!firstProfile || !addedProfile) return [];
    return [
      {
        label: `${firstProfileName} -> ${addedProfile}`,
        value: `${firstProfile.id} -> ${addedProfileId}`,
      },
      {
        label: `${addedProfile} -> ${firstProfileName}`,
        value: `${addedProfileId} -> ${firstProfile.id}`,
      },
    ];
  }, [firstProfile, firstProfileName, addedProfile, addedProfileId]);

  const profileOptions: Option[] = useMemo(() => {
    const profileData = profileList?.data as any;
    const profiles = Array.isArray(profileData)
      ? profileData
      : profileData?.results ?? [];

    const filtered =
      profiles
        ?.filter(
          (item: any) =>
            item._type === partyType &&
            String(item.id) !== String(firstProfile?.id),
        )
        ?.map(({ first_name, last_name, id, name, _type }: any) => ({
          label: _type === "entity" ? name : first_name + " " + last_name,
          value: id,
          icon: _type,
        })) ?? [];

    return [CREATE_NEW_OPTION, ...filtered];
  }, [profileList, partyType, firstProfile?.id]);

  const handleBack = (values: any) => {
    if (step === 1) {
      setAddRelationshipBoxOpen(false);
      return;
    }

    if (step === 2) {
      setStep(1);
      return;
    }

    if (step === 3) {
      setStep(values.profile === CREATE_NEW_OPTION.value ? 2 : 1);
      return;
    }

    if (step === 4) {
      setStep(3);
    }
  };
  const closeForm = () => {
    setAddRelationshipBoxOpen(false);
    setStep(1);
  };

  const isCreateNewProfileStepValid = (values: any) => {
    if (values.profile !== CREATE_NEW_OPTION.value) return true;

    if (values.partyType === "individual") {
      return Boolean(values.gender && values.firstName && values.lastName);
    }

    if (values.partyType === "entity") {
      return Boolean(values.name && values.entityType);
    }

    return false;
  };

  const isRelationshipStepValid = (values: any) => {
    if (!values.direction) return false;

    const controls = values.controls || {};
    const enabledControls = Object.entries(controls).filter(
      ([_, control]: any) => control?.enabled,
    );

    if (enabledControls.length === 0) return false;

    return enabledControls.every(([id, control]: any) => {
      const relationType = (profileRelationTypes || []).find(
        (item: any) => String(item.id) === String(id),
      );

      if (relationType?.percentage_required && !control?.percentage) {
        return false;
      }

      if (relationType?.description_required && !control?.description) {
        return false;
      }

      return true;
    });
  };

  const isNextDisabled = (values: any) => {
    if (step === 1) {
      return !values.partyType || !values.profile;
    }

    if (step === 2) {
      return !isCreateNewProfileStepValid(values);
    }

    if (step === 3) {
      return !isRelationshipStepValid(values);
    }

    if (step === 4) {
      return false;
    }

    return true;
  };

  if (!addRelationshipBoxOpen) return null;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Slider
        isOpen={addRelationshipBoxOpen}
        onRequestClose={() => closeForm()}
        width="600px"
      >
        <Box overflowY="auto" height={"calc(100vh - 211px)"}>
          {/* STEP 1 */}
          <>
            <Formik
              validateOnMount
              initialValues={INITIAL_FORM_VALUES}
              onSubmit={handleSubmit}
            >
              {(props) => (
                <form
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (step === 4) {
                      props.handleSubmit();
                    }
                  }}
                >
                  {step === 1 && (
                    <>
                      <Box px={4} py={3}>
                        <H2>Select Profile</H2>
                        <Paragraph>
                          Please select the profile you want to add a
                          relationship to
                        </Paragraph>
                        <Select
                          label="Natural Person or Entity?"
                          name="partyType"
                          value={
                            PARTY_TYPE_OPTIONS.find(
                              (o) => o.value === props.values.partyType,
                            ) ?? null
                          }
                          onChange={async (o: Option | null) => {
                            const pt =
                              (o?.value as "individual" | "entity") ?? "";

                            await props.setFieldValue("partyType", pt, true);
                            props.setFieldTouched("partyType", true, false);
                            props.setFieldError("partyType", undefined);
                            setPartyType(o?.value);
                          }}
                          options={PARTY_TYPE_OPTIONS}
                          placeholder="Choose natural person or entity"
                          isRequired
                          hasError={
                            !!props.touched.partyType &&
                            !!props.errors.partyType
                          }
                          errorMessage={props.errors.partyType as string}
                          mt={5}
                        />

                        {props.values.partyType !== "" && (
                          <Select
                            label="Search Profile"
                            name="profile"
                            value={
                              profileOptions.find(
                                (o) => o.value === props.values.profile,
                              ) ?? null
                            }
                            onChange={async (o: Option | null) => {
                              if (!o) return;
                              //handle create new
                              if (o.value === "CREATE_NEW_PROFILE") {
                                setStep(2);
                              }

                              await props.setFieldValue(
                                "profile",
                                o.value,
                                true,
                              );
                              props.setFieldValue(
                                "profileLabel",
                                o.label,
                                true,
                              );
                              props.setFieldTouched("profile", true, false);
                              props.setFieldError("profile", undefined);
                              //setAddedProfile(o.value);
                            }}
                            options={profileOptions}
                            placeholder="Search"
                            isRequired
                            hasError={
                              !!props.touched.profile && !!props.errors.profile
                            }
                            errorMessage={props.errors.profile as string}
                            mt={5}
                          />
                        )}
                      </Box>
                    </>
                  )}

                  {step === 2 &&
                    props.values.profile === "CREATE_NEW_PROFILE" &&
                    props.values.partyType === "individual" && (
                      <Box px={4} py={3}>
                        <H2>Add New Person </H2>
                        <Paragraph>
                          Please select the type of application you would like
                          to create
                        </Paragraph>

                        <Flex gap="16" mb={3} flexWrap="wrap">
                          <Box flex={1} minWidth={200}>
                            <Select
                              flex={1}
                              options={titleOptions}
                              placeholder="Select title"
                              value={titleOptions.find(
                                (o) => o.value === props.values.title,
                              )}
                              onChange={(option: any) =>
                                props.setFieldValue("title", option.value)
                              }
                              name="title"
                              label="Title"
                              hasError={Boolean(props.errors.title)}
                              errorMessage={props.errors.title as string}
                            />
                          </Box>

                          <Box flex={1} minWidth={200}>
                            <Select
                              flex={1}
                              options={genderOptions}
                              placeholder="Select gender"
                              value={genderOptions.find(
                                (o) => o.value === props.values.gender,
                              )}
                              onChange={(option: any) =>
                                props.setFieldValue("gender", option.value)
                              }
                              name="gender"
                              isRequired
                              label="Gender"
                              hasError={Boolean(props.errors.gender)}
                              errorMessage={props.errors.gender as string}
                            />
                          </Box>
                        </Flex>

                        <Flex gap="16" mb={3} flexWrap="wrap">
                          <Box flex={1} minWidth={200}>
                            <Input
                              placeholder="Enter first name"
                              name="firstName"
                              value={props.values.firstName}
                              onChange={props.handleChange}
                              hasError={Boolean(props.errors.firstName)}
                              errorMessage={props.errors.firstName as string}
                              isRequired
                              label="First name"
                            />
                          </Box>

                          <Box flex={1} minWidth={200}>
                            <Input
                              placeholder="Enter middle name(s)"
                              name="middleName"
                              value={props.values.middleName}
                              onChange={props.handleChange}
                              hasError={Boolean(props.errors.middleName)}
                              errorMessage={props.errors.middleName as string}
                              label="Middle name(s)"
                            />
                          </Box>
                        </Flex>

                        <Flex gap="16" mb={3} flexWrap="wrap">
                          <Box flex={1} minWidth={200}>
                            <Input
                              placeholder="Enter last name"
                              name="lastName"
                              value={props.values.lastName}
                              onChange={props.handleChange}
                              hasError={Boolean(props.errors.lastName)}
                              errorMessage={props.errors.lastName as string}
                              isRequired
                              label="Last name"
                            />
                          </Box>

                          <Box flex={1} minWidth={200}>
                            <DatePicker
                              label="Date of birth"
                              value={props.values.dateOfBirth as any}
                              onChange={(date: any) =>
                                props.setFieldValue("dateOfBirth", date)
                              }
                              maxDate={DateTime.now()
                                .minus({ years: MIN_AGE_APPLICATION })
                                .toJSDate()}
                              name="dateOfBirth"
                              hasError={Boolean(props.errors.dateOfBirth)}
                              errorMessage={props.errors.dateOfBirth as string}
                            />
                          </Box>
                        </Flex>

                        <Box mb={3}>
                          <Input
                            placeholder="Enter email address"
                            name="email"
                            value={props.values.email}
                            onChange={props.handleChange}
                            hasError={Boolean(props.errors.email)}
                            label="Email address"
                            errorMessage={props.errors.email as string}
                          />
                        </Box>
                      </Box>
                    )}

                  {step === 2 &&
                    props.values.profile === "CREATE_NEW_PROFILE" &&
                    props.values.partyType === "entity" && (
                      <Box px={4} py={3}>
                        <H2>New Entity</H2>

                        <Input
                          placeholder="Entity name"
                          name="name"
                          value={props.values.name}
                          onChange={props.handleChange}
                          hasError={Boolean(props.errors.name)}
                          errorMessage={props.errors.name as string}
                          isRequired
                          label="Entity name"
                        />

                        <Select
                          value={countryOptions.find(
                            (o) => o.value === props.values.country,
                          )}
                          options={countryOptions}
                          onChange={(option: any) =>
                            props.setFieldValue("country", option.value)
                          }
                          minWidth={200}
                          flex={1}
                          label="Country of registration/establishment"
                          name="country"
                          mb={3}
                          mt={3}
                        />

                        <Flex gap="16" mb={3}>
                          <Box flex={1}>
                            <Select
                              value={entityTypeOptions.find(
                                (o) => o.value === props.values.entityType,
                              )}
                              options={entityTypeOptions}
                              onChange={(option: any) =>
                                props.setFieldValue("entityType", option.value)
                              }
                              minWidth={200}
                              flex={1}
                              label="Entity Type"
                              name="entity_type"
                              isRequired
                            />
                          </Box>

                          <Box flex={1}>
                            <Select
                              value={companyStatusOptions.find(
                                (o) => o.value === props.values.status,
                              )}
                              options={companyStatusOptions}
                              onChange={(option: any) =>
                                props.setFieldValue("status", option.value)
                              }
                              minWidth={200}
                              flex={1}
                              label="Current status"
                              name="status"
                            />
                          </Box>
                        </Flex>

                        <Flex gap="16">
                          <Box flex={1}>
                            <Input
                              placeholder="Registration number"
                              label="Registration number"
                              value={props.values.registrationNumber}
                              onChange={props.handleChange}
                            />
                          </Box>
                          <Box flex={1}>
                            <DatePicker
                              label="Date of registration"
                              value={props.values.dateOfRegistration as any}
                              onChange={(date: any) =>
                                props.setFieldValue("dateOfRegistration", date)
                              }
                              maxDate={DateTime.now().toJSDate()}
                              name="dateOfRegistration"
                              hasError={Boolean(
                                props.errors.dateOfRegistration,
                              )}
                              errorMessage={
                                props.errors.dateOfRegistration as string
                              }
                            />
                          </Box>
                        </Flex>
                      </Box>
                    )}
                  {step === 3 && (
                    <Box px={4} py={3}>
                      <H2>Define Relationship</H2>
                      <Paragraph>
                        Please define the details for this relationship
                      </Paragraph>
                      <Box mt={2}>
                        <Select
                          placeholder="Specify direction"
                          label="Specify direction"
                          value={relationshipOptions.find(
                            (o) => o.value === props.values.direction,
                          )}
                          onChange={(option: any) => {
                            const [source, target] = option.value
                              .split("->")
                              .map((v: string) => v.trim());
                            setSource(source);
                            setTarget(target);

                            props.setFieldValue("direction", option.value);
                          }}
                          options={relationshipOptions}
                        />
                      </Box>
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        mt={3}
                        mb={3}
                      >
                        <Paragraph>Choose type of relationships</Paragraph>

                        <Flex alignItems="center" gap="8">
                          <Label fontSize={2}>Filter by entity type</Label>
                          <Toggle
                            isChecked={filterByEntityType}
                            onChange={(e) =>
                              setFilterByEntityType(e.target.checked)
                            }
                          />
                        </Flex>
                      </Flex>

                      {(profileRelationTypes || []).map((r: any) => {
                        const path = `controls.${r.id}`;
                        const val = (props.values as any).controls?.[r.id] ?? {
                          enabled: false,
                          percentage: "",
                          description: "",
                          type: "",
                        };
                        const touched =
                          (props.touched as any)?.controls?.[r.id] || {};
                        const errors =
                          (props.errors as any)?.controls?.[r.id] || {};

                        return (
                          <Box
                            key={r.id}
                            bg="white"
                            borderRadius="12px"
                            p={3}
                            mb={3}
                            border="1px solid"
                            borderColor="gray.10"
                          >
                            <Flex
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Box>
                                <Label fontSize={2}>{r.name}</Label>
                                {r.narrative && (
                                  <Paragraph
                                    mt={0}
                                    fontSize={1}
                                    color="gray.60"
                                  >
                                    {r.narrative}
                                  </Paragraph>
                                )}
                              </Box>

                              <Toggle
                                isChecked={val.enabled}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  props.setFieldValue(
                                    `${path}.enabled`,
                                    checked,
                                  );
                                  props.setFieldValue(`${path}.type`, r.name);

                                  if (r.percentage_required) {
                                    props.setFieldTouched(
                                      `${path}.percentage`,
                                      checked,
                                      false,
                                    );
                                    if (!checked)
                                      props.setFieldValue(
                                        `${path}.percentage`,
                                        "",
                                      );
                                  }
                                  if (r.description_required) {
                                    props.setFieldTouched(
                                      `${path}.description`,
                                      checked,
                                      false,
                                    );
                                    if (!checked)
                                      props.setFieldValue(
                                        `${path}.description`,
                                        "",
                                      );
                                  }
                                }}
                              />
                            </Flex>

                            {val.enabled && (
                              <Box mt={3}>
                                {r.percentage_required && (
                                  <Box mb={3}>
                                    <Input
                                      label="Percent (%)"
                                      name={`${path}.percentage`}
                                      placeholder="Provide percentage"
                                      value={val.percentage ?? ""}
                                      onChange={(e) => {
                                        const next = e.target.value;
                                        props.setFieldValue(
                                          `${path}.percentage`,
                                          next,
                                        );
                                      }}
                                      onBlur={() =>
                                        props.setFieldTouched(
                                          `${path}.percentage`,
                                          true,
                                        )
                                      }
                                      isRequired
                                      hasError={Boolean(
                                        touched.percentage && errors.percentage,
                                      )}
                                      errorMessage={
                                        touched.percentage
                                          ? errors.percentage
                                          : ""
                                      }
                                    />
                                  </Box>
                                )}

                                {r.description_required && (
                                  <TextArea
                                    label="Description"
                                    name={`${path}.description`}
                                    placeholder="Provide description"
                                    value={val.description}
                                    onChange={(e) =>
                                      props.setFieldValue(
                                        `${path}.description`,
                                        e.target.value,
                                      )
                                    }
                                    isRequired
                                    hasError={Boolean(
                                      touched.description && errors.description,
                                    )}
                                    errorMessage={
                                      touched.description
                                        ? errors.description
                                        : ""
                                    }
                                  />
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {step === 4 && (
                    <Box px={4} py={3}>
                      <H2>Confirm Relationship</H2>
                      <Paragraph>
                        Please confirm the relationship(s) of these profiles
                      </Paragraph>

                      <H4>Profiles</H4>
                      <Flex gap={12} justifyContent="space-between">
                        <GrayCard padding="0" width="50%">
                          <Paragraph>{firstProfileName}</Paragraph>
                        </GrayCard>
                        <Icon
                          Type={CgChevronRight}
                          aria-label="Close alert"
                          color="gray.50"
                          size="20"
                          alignContent={"center"}
                        />
                        <GrayCard padding="0" width="50%">
                          <Paragraph>{addedProfile}</Paragraph>
                        </GrayCard>
                      </Flex>
                      <H4>Relationship Details</H4>
                      <GrayCard padding="0">
                        <Flex direction="column" gap="24px">
                          <Box flex={1}>
                            <CardLine>Type</CardLine>
                          </Box>
                          <Box flex={1}>
                            <CardLine>Percentage</CardLine>
                          </Box>
                          <Box flex={1}>
                            <CardLine>Description</CardLine>
                          </Box>
                        </Flex>

                        {props.values.controls &&
                          Object.values(props.values.controls)
                            .filter((r: any) => r.enabled)
                            .map((r: any, i: number) => (
                              <Flex key={i} gap="24px" mt="5px">
                                <Box flex={1}>
                                  <Paragraph>{r.type}</Paragraph>
                                </Box>
                                <Box flex={1} ml="5px">
                                  <Paragraph>{r.percentage}</Paragraph>
                                </Box>
                                <Box flex={1} ml="5px">
                                  <Paragraph>{r.description}</Paragraph>
                                </Box>
                              </Flex>
                            ))}
                      </GrayCard>
                    </Box>
                  )}
                  <Flex justifyContent="space-between" margin={3}>
                    <Button onClick={() => handleBack(props.values)}>
                      {step === 1 ? "Cancel" : "Back"}
                    </Button>

                    <Button
                      type="button"
                      isDisabled={isNextDisabled(props.values)}
                      onClick={() => {
                        if (step < 4) {
                          handleNext(props);
                        } else {
                          props.submitForm();
                        }
                      }}
                    >
                      {step === 4 ? "Create Relationship" : "Next"}
                    </Button>
                  </Flex>
                </form>
              )}
            </Formik>
          </>

          <hr />
        </Box>
        <Modal
          isOpen={showSuccess}
          onClose={handleCloseSuccess}
          title="Relationship(s) created"
          content="The relationships between these profile have been created."
          primaryButtonText="Close"
          primaryButtonVariant="primary"
          onModalConfirmClick={handleCloseSuccess}
          width="450px"
          hasIcon={true}
          isSuccessful={true}
        />
      </Slider>
    </div>
  );
};

export default RelationshipSlider;
